import React, { useEffect, useMemo } from 'react';
import { injectIntl, WrappedComponentProps } from '@kyruus/intl';
import {
  PMCConfig,
  PMCLogoSections,
  ProviderMatchProviderV9
} from '@kyruus/types';
import {
  getProviderLogoObjects,
  ProviderLogos
} from '@kyruus/provider-components';

import messages from '../../lib/messages';

import {
  NetworkAffiliationsList,
  NetworkAffiliationsListItem,
  LogosContainer
} from './network-affiliations-styles';
import { ProviderAffiliationsCard, CardContentHeader } from './styles';
import {
  isSummaryDataConfigured,
  pruneIgnoredNetworkAffiliations
} from '../../lib/utils';
import { withErrorBoundary } from '../ErrorFallback/with-error-boundary';

interface ProviderNetworkAffiliationsProps {
  provider: ProviderMatchProviderV9;
  config: PMCConfig;
  shouldSkipRender: (component: string) => void;
}

const ProviderNetworkAffiliations = ({
  intl,
  provider,
  config,
  shouldSkipRender
}: ProviderNetworkAffiliationsProps &
  WrappedComponentProps): React.ReactElement | null => {
  // prune ignored network_affiliations off the provider object
  const prunedProvider = pruneIgnoredNetworkAffiliations(provider, config);
  // only display logos that are mapped to network_affiliations field values
  const networkAffiliationsLogos = (config.logos_v9 || []).filter(
    (logoConfig) => logoConfig.field === 'network_affiliations'
  );
  const providerLogos = useMemo(
    () =>
      getProviderLogoObjects(
        prunedProvider,
        networkAffiliationsLogos,
        config.facets_v9 || [],
        PMCLogoSections.PROFILE
      ),
    [prunedProvider, networkAffiliationsLogos, config]
  );
  const hasLogos = providerLogos.length > 0;

  const skipRendering =
    !isSummaryDataConfigured('network_affiliations', config) ||
    (!prunedProvider.network_affiliations?.length && !hasLogos);

  useEffect(() => {
    if (skipRendering) {
      // callback to the parent component to skip rendering
      shouldSkipRender('network-affiliations');
    }
  }, [skipRendering]);

  if (skipRendering) {
    return null;
  }

  return (
    <ProviderAffiliationsCard
      id={'info-card-network-affiliations'}
      data-testid={'info-card-network-affiliations'}
    >
      <CardContentHeader>
        {intl.formatMessage(messages.profileNetworkAffiliationsHeader)}
      </CardContentHeader>

      <NetworkAffiliationsList hasLogos={hasLogos}>
        {prunedProvider.network_affiliations.map(({ name }, index) => (
          <NetworkAffiliationsListItem hasLogos={hasLogos} key={name + index}>
            {name}
          </NetworkAffiliationsListItem>
        ))}
      </NetworkAffiliationsList>
      <LogosContainer>
        <ProviderLogos
          provider={prunedProvider}
          logoConfigs={networkAffiliationsLogos}
          facets={config.facets_v9}
          logoSection={PMCLogoSections.PROFILE}
          isVertical={false}
        />
      </LogosContainer>
    </ProviderAffiliationsCard>
  );
};

export default withErrorBoundary(injectIntl(ProviderNetworkAffiliations));
